import React, { useEffect, useState } from 'react';
import { Bell, Package, AlertTriangle, Clock } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const AdminNotifications = () => {
  const { theme, darkMode } = useAdminTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('adminToken');

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      const pending = response.data?.orders_by_status?.pending || 0;
      const latestPendingAt = response.data?.latest_pending_order_created_at;
      const lowStockProducts = response.data?.low_stock_products || [];
      const lowStockThreshold = response.data?.low_stock_threshold;

      const next = [];

      if (pending > 0) {
        next.push({
          id: 'pending_orders',
          type: 'orders',
          title: pending === 1 ? '1 pedido pendente' : `${pending} pedidos pendentes`,
          time: formatDateTime(latestPendingAt),
          read: false,
        });
      }

      if (lowStockProducts.length > 0) {
        const thresholdText = typeof lowStockThreshold === 'number' ? ` (<= ${lowStockThreshold})` : '';
        next.push({
          id: 'low_stock',
          type: 'stock',
          title: lowStockProducts.length === 1
            ? `1 produto com estoque baixo${thresholdText}`
            : `${lowStockProducts.length} produtos com estoque baixo${thresholdText}`,
          time: '—',
          read: false,
        });
      }

      setNotifications(next);
    } catch (error) {
      // handled by AdminLayout redirect on most pages; keep silent here
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [token]);

  const iconFor = (type) => {
    if (type === 'orders') return Package;
    if (type === 'stock') return AlertTriangle;
    return Bell;
  };

  return (
    <AdminLayout title="Notificações" subtitle="Todas as notificações do painel">
      <div className="p-6 space-y-6">
        <Card className={`${theme.card} border`}
        >
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <Bell className={theme.textMuted} />
              </div>
              <div>
                <CardTitle className={theme.text}>Notificações</CardTitle>
                <p className={`text-sm ${theme.textMuted}`}>Atualiza automaticamente a cada 15s</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className={`text-sm ${theme.textMuted}`}>Carregando...</div>
            ) : notifications.length === 0 ? (
              <div className={`text-sm ${theme.textMuted}`}>Nenhuma notificação no momento.</div>
            ) : (
              <div className="space-y-3">
                {notifications.map((n) => {
                  const Icon = iconFor(n.type);
                  return (
                    <div
                      key={n.id}
                      className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 ${darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                          <Icon className={`h-5 w-5 ${theme.text}`} />
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${theme.text}`}>{n.title}</p>
                          <div className={`mt-1 flex items-center gap-2 text-xs ${theme.textMuted}`}>
                            <Clock className="h-3.5 w-3.5" />
                            <span>{n.time}</span>
                          </div>
                        </div>
                      </div>
                      <div className={`text-xs ${theme.textMuted}`}>{n.read ? 'Lida' : 'Não lida'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
